import { useMemo } from 'react';

import { buildReportSummaries } from './approvalQueueModel';
import { buildBlackoutQueueItem } from './tabs/blackoutDates/blackoutQueue';
import type { BlackoutCoverageSummary } from './tabs/blackoutDates/types';
import { buildBundledExperienceQueueItem } from './tabs/bundledExperiences/bundledExperiencesQueue';
import { buildCampaignQueueItem } from './tabs/campaign/campaignQueue';
import { buildDatesRestrictionsQueueItem } from './tabs/datesRestrictions/datesRestrictionsQueue';
import { buildDealOptionsInclusionsQueueItem } from './tabs/dealOptionsInclusions/dealOptionsInclusionsQueue';
import { buildCapacityQueueItem, buildMarginQueueItem } from './tabs/egs/egsQueue';
import { buildSetupQueueItem } from './tabs/setup/setupQueue';
import { buildSurchargeTableQueueItem, buildSurchargesQueueItem } from './tabs/surcharges/surchargesQueue';
import { buildTaxesPaymentsQueueItem } from './tabs/taxesPayments/taxesPaymentsQueue';
import type { ChecklistRowsByCategory, ManualApprovalState } from './types';
import type { VisibleChecklistRows } from './useVisibleChecklistRows';

interface Input {
  blackoutCoverage: BlackoutCoverageSummary;
  expandedQueueItems: Record<string, boolean>;
  manualApprovals: ManualApprovalState;
  offerId: string;
  propertyId?: string;
  rows: ChecklistRowsByCategory;
  vendorId?: string;
  visibleRows: VisibleChecklistRows;
}

export function useApprovalQueueModel({
  blackoutCoverage,
  expandedQueueItems,
  manualApprovals,
  offerId,
  propertyId,
  rows,
  vendorId,
  visibleRows,
}: Input) {
  const visibleQueueItems = useMemo(
    () => [
      ...visibleRows.visibleSetupRows.map((row) => buildSetupQueueItem(row)),
      ...visibleRows.visibleCampaignRows.map((row) => buildCampaignQueueItem(row)),
      ...visibleRows.visibleDatesRestrictionsRows.map((row) => buildDatesRestrictionsQueueItem(row)),
      ...visibleRows.visibleBlackoutRows.map((row) => buildBlackoutQueueItem(row, blackoutCoverage)),
      ...visibleRows.visibleMarginRows.map((row) => buildMarginQueueItem(row)),
      ...visibleRows.visibleCapacityRows.map((row) => buildCapacityQueueItem(row)),
      ...visibleRows.visibleDealOptionsInclusionsRows.map((row) => buildDealOptionsInclusionsQueueItem(row)),
      ...visibleRows.visibleSurchargeTableRows.map((row) => buildSurchargeTableQueueItem(row)),
      ...visibleRows.visibleSurchargeRules.map((row) => buildSurchargesQueueItem(row)),
      ...visibleRows.visibleTaxesPaymentsRows.map((row) => buildTaxesPaymentsQueueItem(row)),
      ...visibleRows.visibleBundledExperienceRows.map((row) => buildBundledExperienceQueueItem(row)),
    ],
    [blackoutCoverage, visibleRows],
  );
  const reportSummaries = useMemo(
    () =>
      buildReportSummaries({
        blackoutRows: rows.blackoutRows,
        bundledExperienceRows: rows.bundledExperienceRows,
        baseSurchargesRows: rows.baseSurchargesRows,
        taxesPaymentsRows: rows.taxesPaymentsRows,
        capacityRows: rows.capacityRows,
        campaignRows: rows.campaignRows,
        datesRestrictionsRows: rows.datesRestrictionsRows,
        dealOptionsInclusionsRows: rows.dealOptionsInclusionsRows,
        manualApprovals,
        marginRows: rows.marginRows,
        offerId,
        propertyId,
        setupRows: rows.setupRows,
        surchargeTableRows: rows.surchargeTableRows,
        vendorId,
      }),
    [manualApprovals, offerId, propertyId, rows, vendorId],
  );
  const areVisibleQueueItemsExpanded =
    visibleQueueItems.length > 0 && visibleQueueItems.every((queueItem) => expandedQueueItems[queueItem.id]);

  return {
    areVisibleQueueItemsExpanded,
    reportSummaries,
    visibleQueueItems,
  };
}
